import { useEffect, useState } from "react";
import { CloudArrowDown, Sparkle } from "@phosphor-icons/react";

/**
 * Detects drag events from outside the page (dragging URLs, tabs, links from
 * other apps) and reports them to the parent. Renders a fullscreen overlay
 * while dragging is happening so the user knows where to drop.
 */
export default function DropZone({ enabled, onDropUrls, processing }) {
  const [isOver, setIsOver] = useState(false);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const hasFilesOrUri = (e) => {
      try {
        const types = Array.from(e?.dataTransfer?.types || []);
        return (
          types.includes("text/uri-list") ||
          types.includes("text/plain") ||
          types.includes("text/html") ||
          types.includes("Files")
        );
      } catch {
        return false;
      }
    };

    const handleEnter = (e) => {
      try {
        if (!hasFilesOrUri(e)) return;
        e.preventDefault();
        setCounter((c) => c + 1);
        setIsOver(true);
      } catch {}
    };
    const handleOver = (e) => {
      try {
        if (!hasFilesOrUri(e)) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      } catch {}
    };
    const handleLeave = (e) => {
      try {
        e.preventDefault();
        setCounter((c) => {
          const next = Math.max(0, c - 1);
          if (next === 0) setIsOver(false);
          return next;
        });
      } catch {}
    };
    const handleDrop = async (e) => {
      try {
        e.preventDefault();
      } catch {}
      setIsOver(false);
      setCounter(0);
      if (!e?.dataTransfer) return;

      const urls = new Set();

      // Prefer text/uri-list (one URL per line, # comments)
      let uriList = "";
      try {
        uriList = e.dataTransfer.getData("text/uri-list") || "";
      } catch {}
      if (uriList) {
        uriList
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter((s) => s && !s.startsWith("#"))
          .forEach((s) => urls.add(s));
      }

      // Fall back to text/plain (often the URL or text containing URLs)
      let plain = "";
      try {
        plain = e.dataTransfer.getData("text/plain") || "";
      } catch {}
      if (plain) {
        const trimmed = plain.trim();
        if (/^https?:\/\/\S+$/i.test(trimmed)) {
          urls.add(trimmed);
        } else {
          const matches = trimmed.match(/https?:\/\/[^\s)<>"']+/gi);
          if (matches) matches.forEach((u) => urls.add(u));
        }
      }

      // Fall back to text/html (e.g. when dragging a link, the HTML has <a href>)
      let html = "";
      try {
        html = e.dataTransfer.getData("text/html") || "";
      } catch {}
      if (html && urls.size === 0) {
        const m = html.match(/href=["']([^"']+)["']/i);
        if (m && m[1] && /^https?:\/\//i.test(m[1])) urls.add(m[1]);
      }

      const list = Array.from(urls);
      if (list.length === 0) return;
      onDropUrls(list);
    };

    window.addEventListener("dragenter", handleEnter);
    window.addEventListener("dragover", handleOver);
    window.addEventListener("dragleave", handleLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleEnter);
      window.removeEventListener("dragover", handleOver);
      window.removeEventListener("dragleave", handleLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [enabled, onDropUrls]);

  if (!enabled) return null;
  if (!isOver && !processing) return null;

  return (
    <div
      data-testid="drop-zone-overlay"
      className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center"
    >
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-[#0A0B10]/80 backdrop-blur-md" />
      {/* Animated border frame */}
      <div className="absolute inset-4 md:inset-8 rounded-3xl border-2 border-dashed border-[#2563EB] animate-pulse" />

      <div className="relative flex flex-col items-center gap-4 px-8 text-center">
        <div className="w-24 h-24 rounded-3xl bg-[#2563EB] flex items-center justify-center deck-tile">
          {processing ? (
            <Sparkle size={48} weight="fill" className="text-white animate-spin" />
          ) : (
            <CloudArrowDown size={48} weight="fill" className="text-white" />
          )}
        </div>
        <div className="font-heading font-black text-3xl md:text-5xl text-white">
          {processing ? "Creando tarjetas..." : "Suelta para añadir"}
        </div>
        <div className="font-mono text-xs md:text-sm text-white/60 max-w-md">
          {processing
            ? "Generando vista previa de cada link"
            : "Arrastra una pestaña, link o texto con URL y se convierte en tarjeta automáticamente"}
        </div>
        <div className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FACC15] text-black font-mono text-[11px] uppercase tracking-[0.18em] font-bold">
          ✨ Detección automática
        </div>
      </div>
    </div>
  );
}
